import React from "react";
import { useLocation } from "react-router-dom";

import { Breadcrumb, Breadcrumbs as PyBreadcrumbs } from "@performyard/py-components";

import { useIsNewNavigation } from "../../../api/hooks/defs/navigation";
import { useEmployeeDashboardOrigin } from "../../../hooks/useEmployeeDashboardOrigin";

import {
	ANALYTICS_LANDING_HREF,
	DASHBOARD_ORIGIN,
	isFreshTabLoad,
	parseReportOrigin,
} from "../../../utils/breadcrumbOrigin";
import { REVIEW_CYCLES_LIST_HREF, parseReviewsOrigin, parseViaReview } from "../../../utils/reviewsBreadcrumbs";

type FormAuthoringBreadcrumbsProps = {
	formName: string;
	reviewId: string;
	reviewName: string;
	cycleName: string;
	canViewReviewCycles: boolean;
};

export const FormAuthoringBreadcrumbs = ({
	formName,
	reviewId,
	reviewName,
	cycleName,
	canViewReviewCycles,
}: FormAuthoringBreadcrumbsProps): React.ReactElement | null => {
	const isNewNav = useIsNewNavigation();
	const location = useLocation();
	const dashboardOrigin = useEmployeeDashboardOrigin({ deep: true });

	if (!isNewNav) return null;
	if (!formName) return null;
	// A dashboard origin exists but its employee name is still resolving: render nothing rather
	// than flashing the generic "Dashboard" root and relabeling.
	if (dashboardOrigin.status === "loading") return null;

	const { from, label, kind } = parseReviewsOrigin(location.search);
	const viaReview = parseViaReview(location.search);
	const reviewOriginHref =
		dashboardOrigin.status === "resolved" && dashboardOrigin.viaHref ? dashboardOrigin.viaHref : from;

	// The form is viewed inside a review, so the parent review crumb appears for
	// every origin that passes through the review detail page (review + cycle).
	// Carry the origin forward so the review page also roots on where the user came from.
	const reviewHref = reviewOriginHref
		? `/view-review/${reviewId}/?from=${encodeURIComponent(reviewOriginHref)}`
		: `/view-review/${reviewId}/`;
	const reviewCrumb: Breadcrumb | null =
		reviewId && reviewName
			? { pageId: "review-detail", name: reviewName, href: reviewHref, forceHardReload: true }
			: null;

	const formCrumb: Breadcrumb = { pageId: "form-authoring", name: formName, href: location.pathname };

	// Opened from a Reporting & Analytics report: root on "Reporting & Analytics > {Report Name}"
	// instead of the form's parent review.
	const reportOrigin = parseReportOrigin(location.search);

	let items: Breadcrumb[];
	if (reportOrigin) {
		items = [
			{
				pageId: "analytics-parent",
				name: "Reporting & Analytics",
				href: ANALYTICS_LANDING_HREF,
				forceHardReload: true,
			},
			{
				pageId: "analytics-report",
				name: reportOrigin.reportName,
				href: reportOrigin.reportHref,
				forceHardReload: true,
			},
			formCrumb,
		];
	} else if (kind === "cycle") {
		items = [
			...(canViewReviewCycles
				? [
						{
							pageId: "review-cycles",
							name: "Review Cycles",
							href: REVIEW_CYCLES_LIST_HREF,
							forceHardReload: true,
						},
					]
				: []),
			{ pageId: "review-cycle-details", name: cycleName || "Review Cycle", href: from, forceHardReload: true },
			...(reviewCrumb ? [reviewCrumb] : []),
			formCrumb,
		];
	} else if (dashboardOrigin.status === "resolved") {
		items = [
			{
				pageId: "employee-dashboard",
				name: `${dashboardOrigin.employeeName}'s Dashboard`,
				href: dashboardOrigin.from,
				forceHardReload: true,
			},
		];
		if (kind === "review" && dashboardOrigin.viaHref) {
			items.push({
				pageId: "reviews-parent",
				name: `${dashboardOrigin.employeeName}'s Reviews`,
				href: dashboardOrigin.viaHref,
				forceHardReload: true,
			});
		}
		if (kind === "review" || (kind === "direct" && viaReview)) {
			items.push(...(reviewCrumb ? [reviewCrumb] : []));
		}
		items.push(formCrumb);
	} else if (isFreshTabLoad()) {
		// Fresh tab: anchor on Dashboard.
		items = [{ pageId: "dashboard-parent", name: DASHBOARD_ORIGIN.label, href: DASHBOARD_ORIGIN.href }, formCrumb];
	} else if (kind === "review" && label) {
		items = [
			{ pageId: "reviews-parent", name: label, href: from, forceHardReload: true },
			...(reviewCrumb ? [reviewCrumb] : []),
			formCrumb,
		];
	} else if (kind === "direct" && label) {
		items = [
			{ pageId: "reviews-parent", name: label, href: from, forceHardReload: true },
			...(viaReview && reviewCrumb ? [reviewCrumb] : []),
			formCrumb,
		];
	} else {
		// Unknown / no origin: root on the parent review when we know it.
		items = [...(reviewCrumb ? [reviewCrumb] : []), formCrumb];
	}

	return <PyBreadcrumbs items={items} dangerouslySetClassName="form-authoring-breadcrumbs-nav" />;
};
